import type { BlogPostMeta } from './posts'

/**
 * Article-specific heroes live here so the page, image sitemap, and any future
 * visual index can resolve the same asset. Posts not listed here retain the
 * established topic-level fallback.
 */
export const ARTICLE_HEROES: Record<string, string> = {
  'does-evian-have-microplastics':
    '/blog/heroes/does-evian-have-microplastics.webp',
  'does-parchment-paper-have-plastic':
    '/blog/heroes/does-parchment-paper-have-plastic.webp',
  'is-polyester-clothing-plastic':
    '/blog/heroes/is-polyester-clothing-plastic.webp',
  'are-nylon-cooking-utensils-plastic':
    '/blog/heroes/are-nylon-cooking-utensils-plastic.webp',
  'does-colgate-toothpaste-have-microplastics':
    '/blog/heroes/does-colgate-toothpaste-have-microplastics.webp',
  'does-vaseline-have-microplastics':
    '/blog/heroes/does-vaseline-have-microplastics.webp',
  'does-essentia-water-have-microplastics':
    '/blog/heroes/does-essentia-water-have-microplastics.webp',
  'plastic-cutting-board-microplastics':
    '/blog/heroes/plastic-cutting-board-microplastics.webp',
  'do-protein-shaker-bottles-shed-microplastics':
    '/blog/heroes/do-protein-shaker-bottles-shed-microplastics.webp',
  'why-water-tests-miss-small-microplastics':
    '/blog/heroes/lake-geneva-small-microplastics-study.webp',
  'protein-shakes-ranked-microplastics-phthalates':
    '/blog/heroes/protein-shakes-ranked-microplastics-phthalates.webp',
  'does-premier-protein-have-microplastics':
    '/blog/heroes/does-premier-protein-have-microplastics.webp',
  'does-muscle-milk-have-microplastics':
    '/blog/heroes/does-muscle-milk-have-microplastics.webp',
  'drugstore-skincare-microplastics-audited':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-cerave-have-microplastics':
    '/blog/heroes/does-cerave-have-microplastics.webp',
  'does-cetaphil-have-microplastics':
    '/blog/heroes/does-cetaphil-have-microplastics.webp',
  'can-you-microwave-solo-cups':
    '/blog/heroes/can-you-microwave-solo-cups.webp',
  'can-you-microwave-ziploc-bags':
    '/blog/heroes/can-you-microwave-ziploc-bags.webp',
  'nanoplastics-placenta-hormones-study-2026':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',

  // ── Shared heroes, 2026-08-10 ──────────────────────────────────────────────
  // Every image above was commissioned for one article, but most depict a
  // SUBJECT rather than a specific product, so they serve the whole cluster
  // around them. Before this, 199 of 246 articles fell through to one of eight
  // generic topic images, which meant the blog index showed the same eight
  // pictures over and over and every social card in a cluster looked identical.
  //
  // These pairings were made by LOOKING at each image, not by matching slugs.
  // That mattered more than expected — the file named after the placenta study
  // is a petri dish of beads on a lab bench, so it belongs on research articles
  // (blood, brain, arterial plaque) and NOT on pregnancy product articles like
  // breast pumps or pregnancy pillows. Deliberately left on their generic topic
  // hero: face masks, diapers, wet wipes, pregnancy pillows, breast pumps,
  // synthetic underwear, tap-water-by-geography and vitamins — no image on hand
  // actually depicts them, and a wrong picture reads worse than a plain one.

  'are-cans-lined-with-plastic':
    '/blog/heroes/microplastics-in-canned-food.webp',
  'are-nalgene-bottles-safe-microplastics':
    '/blog/heroes/stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics.webp',
  'are-stanley-cups-non-toxic-microplastic-audit':
    '/blog/heroes/stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics.webp',
  'are-vacuum-sealer-bags-safe-microplastics':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'best-electrolyte-powders-without-microplastics':
    '/blog/heroes/protein-shakes-ranked-microplastics-phthalates.webp',
  'best-microplastic-free-toothpaste':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'best-reusable-k-cup-pods-stainless-steel-ranked':
    '/blog/heroes/do-k-cups-release-microplastics.webp',
  'best-reverse-osmosis-systems-for-microplastics':
    '/blog/heroes/microplastics-water-filters-compared.webp',
  'best-stainless-steel-water-bottles':
    '/blog/heroes/refillable-water-bottle-materials-compared.webp',
  'best-sunscreens-without-microplastics-brands-ranked':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'best-water-filter-pitchers-ranked':
    '/blog/heroes/microplastics-water-filters-compared.webp',
  'borosilicate-glass-vs-soda-lime-microplastics':
    '/blog/heroes/best-glass-water-bottles.webp',
  'clean-beauty-microplastics-loophole':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'dishwasher-microplastics-plastic-containers':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'do-brita-filters-remove-microplastics':
    '/blog/heroes/microplastics-water-filters-compared.webp',
  'do-coffee-filters-have-microplastics':
    '/blog/heroes/microplastics-in-coffee-by-brewing-method.webp',
  'do-energy-drinks-have-microplastics':
    '/blog/heroes/protein-shakes-ranked-microplastics-phthalates.webp',
  'do-zyn-pouches-have-microplastics':
    '/blog/heroes/chewing-gum-brands-ranked-microplastics.webp',
  'does-almond-milk-have-microplastics':
    '/blog/heroes/microplastics-in-oat-milk-brands-ranked.webp',
  'does-aquatru-remove-microplastics':
    '/blog/heroes/microplastics-water-filters-compared.webp',
  'does-berkey-filter-microplastics':
    '/blog/heroes/microplastics-water-filters-compared.webp',
  'does-clearly-filtered-remove-microplastics':
    '/blog/heroes/microplastics-water-filters-compared.webp',
  'does-crest-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-dunkin-coffee-contain-microplastics':
    '/blog/heroes/does-starbucks-coffee-contain-microplastics.webp',
  'does-fenty-beauty-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-huda-beauty-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-hydroflask-have-microplastics':
    '/blog/heroes/stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics.webp',
  'does-kylie-cosmetics-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-neutrogena-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-orbit-gum-have-microplastics':
    '/blog/heroes/chewing-gum-brands-ranked-microplastics.webp',
  'does-pur-gum-have-microplastics':
    '/blog/heroes/chewing-gum-brands-ranked-microplastics.webp',
  'does-rhode-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-sensodyne-have-microplastics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'does-simply-gum-have-microplastics':
    '/blog/heroes/chewing-gum-brands-ranked-microplastics.webp',
  'does-yeti-have-microplastics':
    '/blog/heroes/stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics.webp',
  'fruit-vegetables-biggest-microplastic-source-diet':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'how-much-plastic-do-we-eat-per-week':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'how-to-check-cosmetic-ingredients-for-microplastics-2026':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'is-rubbermaid-safe-microplastics':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'is-tritan-a-microplastic':
    '/blog/heroes/refillable-water-bottle-materials-compared.webp',
  'is-tupperware-safe-microplastics':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'microplastics-and-aging-skin-collagen':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-and-alzheimers-dementia':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-and-cancer':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-and-fertility':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-and-miscarriage-risk':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-and-preeclampsia':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-and-skin-absorption':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-in-amniotic-fluid':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-arterial-plaque':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-baby-skincare-and-sunscreen':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-in-bread':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-breast-milk':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-canned-tuna-and-salmon':
    '/blog/heroes/microplastics-in-canned-food.webp',
  'microplastics-in-cereal':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-chocolate':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-coffee':
    '/blog/heroes/microplastics-in-coffee-by-brewing-method.webp',
  'microplastics-in-coffee-creamer':
    '/blog/heroes/microplastics-in-milk-and-dairy.webp',
  'microplastics-in-coffee-cups':
    '/blog/heroes/does-starbucks-coffee-contain-microplastics.webp',
  'microplastics-in-cosmetics':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-in-deodorant-brands-ranked':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-in-disposable-cutlery':
    '/blog/heroes/can-you-microwave-solo-cups.webp',
  'microplastics-in-eggs':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-fast-food':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-fast-food-packaging':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-fruits-and-vegetables':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-honey':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-human-blood':
    '/blog/heroes/microplastics-in-human-blood.webp',
  'microplastics-in-ice-cream':
    '/blog/heroes/microplastics-in-milk-and-dairy.webp',
  'microplastics-in-k-cups-and-coffee-pods':
    '/blog/heroes/do-k-cups-release-microplastics.webp',
  'microplastics-in-kombucha':
    '/blog/heroes/microplastics-in-tea-bags.webp',
  'microplastics-in-makeup-brands-audited':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-in-meat':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-pasta':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-placenta':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-plastic-containers':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'microplastics-in-plastic-wrap':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'microplastics-in-pregnancy':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-pregnancy-by-trimester':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-protein-powder-supplements':
    '/blog/heroes/protein-shakes-ranked-microplastics-phthalates.webp',
  'microplastics-in-rice':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-salt':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-seafood':
    '/blog/heroes/foods-with-the-most-microplastics.webp',
  'microplastics-in-shampoo-and-conditioner':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'microplastics-in-sous-vide':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'microplastics-in-the-brain':
    '/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp',
  'microplastics-in-yogurt':
    '/blog/heroes/microplastics-in-milk-and-dairy.webp',
  'microwaving-plastic-containers-microplastics':
    '/blog/heroes/microwaving-plastic-containers-microplastics.webp',
  'pfas-in-dental-floss':
    '/blog/heroes/drugstore-skincare-microplastics-audited.webp',
  'plastic-wrap-ziploc-bags-microplastics':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'pyrex-vs-anchor-hocking-glass-food-storage':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'red-solo-cups-plastic-party-supplies-microplastics':
    '/blog/heroes/can-you-microwave-solo-cups.webp',
  'styrofoam-takeout-containers-microplastics':
    '/blog/heroes/best-plastic-free-food-storage.webp',
  'what-does-microwave-safe-actually-mean':
    '/blog/heroes/can-you-microwave-ziploc-bags.webp',
}

const TOPIC_HEROES: Record<BlogPostMeta['topic'], string> = {
  water: '/blog/heroes/water.webp',
  food: '/blog/heroes/food.webp',
  pregnancy: '/blog/heroes/baby.webp',
  home: '/blog/heroes/home.webp',
  health: '/blog/heroes/health.webp',
  cosmetics: '/blog/heroes/cosmetics.webp',
  clothing: '/blog/heroes/clothing.webp',
  general: '/blog/heroes/general.webp',
}

export const BLOG_INDEX_HERO = '/blog/heroes/blog-research-desk.webp'

export function getBlogHeroImage(
  post: Pick<BlogPostMeta, 'slug' | 'topic'>
): string {
  return ARTICLE_HEROES[post.slug] ?? TOPIC_HEROES[post.topic]
}
